"use client";

import { HorizontalCategoricalBar } from "@/components/charts/HorizontalCategoricalBar";
import { getPalette } from "@/lib/palette";
import { usePrefersDark } from "@/lib/usePrefersDark";
import type { WardRow } from "@/app/property-taxes/_lib/data";

type Props = {
  data: WardRow[];
};

type RowMeta = {
  parcels: number;
  residential: number;
  nonResidential: number;
  farmLand: number;
};

const BILLIONS = 1_000_000_000;
const MILLIONS = 1_000_000;

function fmtCAD(v: number): string {
  if (v >= BILLIONS) return `$${(v / BILLIONS).toFixed(2)}B`;
  if (v >= MILLIONS) return `$${(v / MILLIONS).toFixed(0)}M`;
  return `$${v.toLocaleString("en-CA")}`;
}

function wardLabel(ward: string): string {
  return ward === "unallocated" ? "unallocated" : `Ward ${ward}`;
}

export function ByWardBars({ data }: Props) {
  const p = getPalette(usePrefersDark());

  // Sort by total revenue desc, push 'unallocated' to the bottom regardless
  // of value so it reads as a footnote rather than a peer ward.
  const sorted = [...data].sort((a, b) => {
    if (a.ward_num === "unallocated") return 1;
    if (b.ward_num === "unallocated") return -1;
    return b.tax_revenue.total - a.tax_revenue.total;
  });

  const rows = sorted.map((r) => ({
    label: wardLabel(r.ward_num),
    value: r.tax_revenue.total,
    color: r.ward_num === "unallocated" ? p.slate : p.teal,
    meta: {
      parcels: r.parcel_count,
      residential: r.tax_revenue.residential.total,
      nonResidential: r.tax_revenue.non_residential.total,
      farmLand: r.tax_revenue.farm_land.total,
    } satisfies RowMeta,
  }));

  return (
    <HorizontalCategoricalBar<RowMeta>
      data={rows}
      height={460}
      xTickFormatter={(v) => fmtCAD(v)}
      valueLabelFormatter={(v) => fmtCAD(v)}
      tooltipLabel={(row) =>
        `${row.label} · ${row.meta?.parcels.toLocaleString("en-CA")} parcels`
      }
      tooltipValueFormatter={(v, row) => {
        if (!row.meta) return fmtCAD(v);
        const { residential, nonResidential, farmLand } = row.meta;
        const parts = [
          `Residential ${fmtCAD(residential)}`,
          `Non-residential ${fmtCAD(nonResidential)}`,
        ];
        if (farmLand > 0) parts.push(`Farm land ${fmtCAD(farmLand)}`);
        return `${fmtCAD(v)} · ${parts.join(" · ")}`;
      }}
      categoryWidth={120}
      sort="none"
    />
  );
}
